import * as analytics from 'analytics';

import { ApiService } from '@/features/api';

import { API_PREFIX } from '../api/constants';
import QuickBooksIcon from '../assets/quickbooks-icon.svg';
import AccountingSoftwareCompany from '../types/AccountingSoftwareCompany';
import AccountingSoftwareContact from '../types/AccountingSoftwareContact';
import Bill from '../types/Bill';
import BillData from '../types/BillData';
import IAccount from '../types/IAccount';
import IBillAttachmentDetails from '../types/IBillAttachmentDetails';
import Invoice from '../types/Invoice';
import InvoiceData from '../types/InvoiceData';
import IOAuthAccessToken from '../types/IOAuthAccessToken';
import IOAuthRequest from '../types/IOAuthRequest';
import IOAuthVerifiedToken from '../types/IOAuthVerifiedToken';
import { SupportedAccountingSoftware as AccountingSoftware } from '../types/SupportedAccountingSoftware';
import setupPopupWindow from '../utils/setupPopupWindow';

import BaseAccountingSoftwarePackage from './BaseAccountingSoftwarePackage';

class QuickBooksOnlineAccountingPackage extends BaseAccountingSoftwarePackage {
  // TODO: remove vendor logo from public dir once we fully migrate to the new UI
  readonly logoUrl = `${import.meta.env.APP_STATIC_APP_ROOT_URL}/img/vendor/quickbooks-icon.svg`;

  readonly icon = QuickBooksIcon;

  supportsInvoices = true;

  constructor() {
    super(AccountingSoftware.QuickBooksOnline);
  }

  ConnectConnectIcon(): string {
    return 'third_party_quickbooks_connect';
  }

  FilterResolutions(conflictingAccountName: string): string {
    return conflictingAccountName;
  }

  // callback that is called via pop-up when OAuth has been completed
  async Connected(info: any): Promise<void> {
    // we would get error if user hit 'No' on Intuit confirmation popup
    if (info.error != undefined) {
      return;
    }

    const companyId: string = this.companyService.CompanyId();

    await ApiService.put(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/oauth`,
      { body: info }
    );

    this.fireAccountingSoftwareConnectedEvent();
    analytics.send('event', 'action', 'completed', 'Connected Quickbooks');
    analytics.send('event', 'Company-AccountingSoftware', 'Connect', 'QBO');
  }

  // called via callback once user clicks on "connect" button
  async Connect(): Promise<any> {
    const connectUrl = `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/quickbooksonline/oauth`;

    const oauthData = await ApiService.get<IOAuthRequest>(connectUrl);

    // set global variable for window so it can be accessed via "window.opener" by callback url
    // @ts-expect-error: Property 'QuickBooksAccountingPackage' does not exist on type 'Window & typeof globalThis'.
    window.QuickBooksAccountingPackage = this;

    const popup = setupPopupWindow({ width: 800, height: 650 });
    if (popup) {
      popup.location.href = oauthData.url;
    }
  }

  async LinkSession(verifiedToken: IOAuthVerifiedToken): Promise<IOAuthVerifiedToken> {
    const token = await ApiService.post<IOAuthVerifiedToken>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/quickbooksonline/link`,
      { body: verifiedToken }
    );
    return token;
  }

  async GetToken(accessToken: IOAuthAccessToken): Promise<IOAuthVerifiedToken> {
    const token = await ApiService.post<IOAuthVerifiedToken>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/quickbooksonline/verify`,
      { body: accessToken }
    );
    return token;
  }

  // generates a clickable link that starts connection process
  GenerateConnectButton($target: JQuery, context?: string): void {
    $('<a/>', {
      href: '#',
      class: 'third_party_quickbooks_connect',
    })
      .appendTo($target)
      .click(() => {
        this.fireAccountingSoftwareConnectingEvent(context);
        this.Connect();
        return false;
      });
  }

  async AddAccount(account: IAccount): Promise<IAccount> {
    const companyId: string = this.companyService.CompanyId();

    const response = await ApiService.put<IAccount>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/accounts`,
      { body: account }
    );

    return response;
  }

  async GetAccounts(): Promise<Array<IAccount>> {
    const companyId: string = this.companyService.CompanyId();

    const accounts = await ApiService.get<Array<IAccount>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/accounts`
    );

    return accounts;
  }

  async fetchCompany(): Promise<AccountingSoftwareCompany> {
    const companyId: string = this.companyService.CompanyId();

    const response = await ApiService.get<AccountingSoftwareCompany>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/company`
    );

    return response;
  }

  async GetBills(): Promise<Array<Bill>> {
    const companyId: string = this.companyService.CompanyId();

    const billData = await ApiService.get<Array<BillData>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/bills`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching bills');
    }

    return billData.map((data) => new Bill(data));
  }

  async GetContacts(): Promise<Array<AccountingSoftwareContact>> {
    const companyId: string = this.companyService.CompanyId();

    const contacts = await ApiService.get<Array<AccountingSoftwareContact>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/contacts`
    );

    return contacts;
  }

  async GetInvoices(): Promise<Array<Invoice>> {
    const companyId: string = this.companyService.CompanyId();

    const invoiceData = await ApiService.get<Array<InvoiceData>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/invoices`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching invoices');
    }

    return invoiceData.map((data) => new Invoice(data));
  }

  async GetBillAttachment(attachment: IBillAttachmentDetails): Promise<void> {
    const popup = setupPopupWindow({ width: 800, height: 650 });
    if (popup) {
      popup.location.href = attachment.url;
    }
  }

  async GetReconciliationStatus(): Promise<string> {
    return 'Reconciled';
  }
}

export default QuickBooksOnlineAccountingPackage;
